import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Card,
  Button,
  Typography,
  Alert,
  Spin,
  Row,
  Col,
  Tag,
  Avatar,
  Divider,
  Space,
  Statistic,
  message,
} from 'antd';
import {
  ScanOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  LoadingOutlined,
  UserOutlined,
  ReloadOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import { attendanceAPI, biometricAPI } from '../../utils/api';
import { useBiometric } from '../../contexts/BiometricContext';

const { Title, Text } = Typography;

// Biometric Attendance Component utilizing global context

// ─── Component ───────────────────────────────────────────────────

const BiometricAttendance = () => {
  const { 
    deviceStatus, 
    scanStatus, 
    lastMatchedUser, 
    detectDevice 
  } = useBiometric();
  
  const [currentTime, setCurrentTime] = useState(new Date());
  const [debugData, setDebugData] = useState(null);
  const [debugLoading, setDebugLoading] = useState(false);

  // Live clock
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const handleTroubleshoot = async () => {
    setDebugLoading(true);
    try {
      const response = await biometricAPI.rdDebug();
      setDebugData(response.data);
    } catch (err) {
      message.error('Failed to run diagnostics.');
    } finally {
      setDebugLoading(false);
    }
  };

  // ─── Status Displays ─────────────────────────────────────────
  const statusConfig = {
    idle: {
      color: '#52c41a',
      icon: <div style={{ 
        width: 64, 
        height: 64, 
        borderRadius: '50%', 
        border: '4px solid #52c41a',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        animation: 'pulse-ring 2s infinite'
      }}>
        <ScanOutlined style={{ fontSize: 32, color: '#52c41a' }} />
      </div>,
      title: 'Ready for Finger',
      subtitle: 'Please place your thumb on the scanner',
    },
    scanning: {
      color: '#52c41a',
      icon: <div style={{ 
        width: 64, 
        height: 64, 
        borderRadius: '50%', 
        border: '4px solid #52c41a',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        animation: 'pulse-ring 2s infinite'
      }}>
        <ScanOutlined style={{ fontSize: 32, color: '#52c41a' }} />
      </div>,
      title: 'Ready for Finger',
      subtitle: 'Please place your thumb on the scanner',
    },
    identifying: {
      color: '#1890ff',
      icon: <LoadingOutlined style={{ fontSize: 64, color: '#1890ff' }} spin />,
      title: 'Identifying...',
      subtitle: 'Searching database for a match',
    },
    success: {
      color: '#52c41a',
      icon: <CheckCircleFilled style={{ fontSize: 64, color: '#52c41a' }} />,
      title: 'Welcome Back!',
      subtitle: 'Attendance recorded successfully',
    },
    failed: {
      color: '#ff4d4f',
      icon: <CloseCircleFilled style={{ fontSize: 64, color: '#ff4d4f' }} />,
      title: 'Scan Error',
      subtitle: 'Please lift and place your finger again',
    },
    no_match: {
      color: '#ff4d4f',
      icon: <CloseCircleFilled style={{ fontSize: 64, color: '#ff4d4f' }} />,
      title: 'Not Recognized',
      subtitle: 'Fingerprint not found in our records',
    },
    already_checkedin: {
      color: '#faad14',
      icon: <ClockCircleOutlined style={{ fontSize: 64, color: '#faad14' }} />,
      title: 'Attendance Exists',
      subtitle: 'You are already checked in for today',
    },
  };

  const current = statusConfig[scanStatus] || statusConfig.idle;
  const isProcessing = scanStatus === 'scanning' || scanStatus === 'identifying';

  return (
    <div style={{ maxWidth: 720, margin: '0 auto', padding: '24px 0' }}>
      {/* Header */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={2} style={{ margin: 0 }}>
            <ScanOutlined style={{ marginRight: 10, color: '#1890ff' }} />
            Biometric Attendance
          </Title>
          <Text type="secondary">Self-service fingerprint check-in</Text>
        </Col>
        <Col>
          <Statistic
            value={currentTime.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            prefix={<ClockCircleOutlined />}
            valueStyle={{ fontSize: 20, fontWeight: 600 }}
          />
          <Text type="secondary" style={{ display: 'block', textAlign: 'right' }}>
            {currentTime.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' })}
          </Text>
        </Col>
      </Row>

      {/* Device Status */}
      {deviceStatus === 'checking' && (
        <Alert type="info" message="Detecting fingerprint device..." icon={<Spin size="small" />} showIcon style={{ marginBottom: 16 }} />
      )}
      {deviceStatus === 'not_found' && (
        <Alert
          type="error"
          message="Fingerprint device not detected"
          description={
            <div>
              <p>The server could not reach the Mantra RD Service. Please ensure:</p>
              <ul style={{ paddingLeft: 20, marginBottom: 8 }}>
                <li>The <strong>Mantra RD Service</strong> is installed and <strong>Running</strong> in <code>services.msc</code></li>
                <li>The <strong>Mantra MFS500</strong> scanner is plugged in via USB</li>
                <li>The backend server is on the same machine as the scanner</li>
              </ul>
              <Space>
                <Button size="small" icon={<ReloadOutlined />} onClick={detectDevice}>Retry Detection</Button>
                <Button
                  size="small"
                  onClick={handleTroubleshoot}
                  loading={debugLoading}
                >
                  Troubleshoot
                </Button>
              </Space>

              {debugData && (
                <div style={{ marginTop: 12, maxHeight: 150, overflowY: 'auto', background: '#000', color: '#0f0', padding: 8, borderRadius: 4, fontFamily: 'monospace', fontSize: '11px', textAlign: 'left' }}>
                  <div style={{ fontWeight: 'bold', borderBottom: '1px solid #0f0', marginBottom: 4 }}>
                    Diagnostic Sweep: {debugData.summary}
                  </div>
                  {debugData.probes.map((p, i) => (
                    <div key={i} style={{ opacity: p.status === 'found' ? 1 : 0.6 }}>
                      {p.protocol}://127.0.0.1:{p.port} - {p.status.toUpperCase()} {p.detail && `(${p.detail})`}
                    </div>
                  ))}
                </div>
              )}
            </div>
          }
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}
      {deviceStatus === 'ready' && (
        <Alert
          type="success"
          message="Biometric Device Ready"
          description="Automatic fingerprint check-in is active."
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Main Scan Card */}
      <Card
        style={{
          borderRadius: 16,
          border: `2px solid ${current.color}`,
          transition: 'all 0.4s ease',
          minHeight: 320,
          textAlign: 'center',
        }}
        bodyStyle={{ padding: '48px 32px' }}
      >
        <div style={{ marginBottom: 24 }}>
          {current.icon}
        </div>

        <Title level={3} style={{ color: current.color, marginBottom: 8 }}>
          {current.title}
        </Title>

        {current.subtitle && (
          <Text type="secondary" style={{ fontSize: 15 }}>
            {current.subtitle}
          </Text>
        )}

        {/* Identified Consumer Card */}
        {lastMatchedUser && (scanStatus === 'success' || scanStatus === 'already_checkedin') && (
          <>
            <Divider />
            <Space direction="vertical" align="center" size={8}>
              <Avatar size={72} icon={<UserOutlined />} style={{ background: '#1890ff' }} />
              <Title level={4} style={{ margin: 0 }}>{lastMatchedUser.name}</Title>
              {lastMatchedUser.center_name && (
                <Tag color="blue">{lastMatchedUser.center_name}</Tag>
              )}
              {scanStatus === 'success' && (
                <Tag color="success" style={{ fontSize: 14, padding: '4px 12px' }}>
                  ✅ Check-In Recorded at {new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                </Tag>
              )}
              {scanStatus === 'already_checkedin' && (
                <Tag color="warning" style={{ fontSize: 14, padding: '4px 12px' }}>
                  ⚠️ Already checked in today
                </Tag>
              )}
            </Space>
          </>
        )}

        {/* Automatic Scan Indicator */}
        <div style={{ marginTop: 36 }}>
          {deviceStatus === 'ready' ? (
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 12,
              padding: '12px 24px',
              borderRadius: '30px',
              background: scanStatus === 'identifying' ? '#e6f7ff' : '#f6ffed',
              border: `1px solid ${scanStatus === 'identifying' ? '#91d5ff' : '#b7eb8f'}`,
              boxShadow: scanStatus === 'identifying' ? '0 0 15px rgba(24, 144, 255, 0.2)' : 'none',
              transition: 'all 0.3s ease'
            }}>
              {scanStatus === 'identifying' ? (
                <LoadingOutlined spin style={{ color: '#1890ff', fontSize: 20 }} />
              ) : (
                <div style={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  background: '#52c41a',
                  animation: 'pulse 1.5s infinite'
                }} />
              )}
              <Text strong style={{ color: scanStatus === 'identifying' ? '#1890ff' : '#52c41a', fontSize: 16 }}>
                {scanStatus === 'identifying' ? 'Processing Fingerprint...' : 'Scanner Ready - Automatic'}
              </Text>
            </div>
          ) : (
            <Button
              type="primary"
              danger
              icon={<ReloadOutlined />}
              onClick={detectDevice}
              style={{ borderRadius: 8, height: 44 }}
            >
              Re-Detect Device
            </Button>
          )}
        </div>

        <style>{`
          @keyframes pulse {
            0% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(82, 196, 26, 0.7); }
            70% { transform: scale(1); box-shadow: 0 0 0 10px rgba(82, 196, 26, 0); }
            100% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(82, 196, 26, 0); }
          }
          @keyframes pulse-ring {
            0% { transform: scale(0.8); opacity: 0.5; }
            50% { transform: scale(1); opacity: 1; }
            100% { transform: scale(0.8); opacity: 0.5; }
          }
        `}</style>
      </Card>

      {/* Instructions */}
      <Card style={{ marginTop: 16, borderRadius: 12 }} size="small">
        <Title level={5} style={{ marginBottom: 8 }}>How to use</Title>
        <ol style={{ paddingLeft: 20, marginBottom: 0, color: '#555', lineHeight: 2 }}>
          <li>Scanning is <strong>automatic</strong> — no need to press any buttons.</li>
          <li>Place your <strong>right or left thumb</strong> on the <strong>Mantra MFS500</strong> fingerprint scanner.</li>
          <li>Hold your finger steady for 2 seconds until the screen changes.</li>
          <li>Your attendance will be automatically marked and the screen will reset for the next person.</li>
        </ol>
      </Card>
    </div>
  );
};

export default BiometricAttendance;
